import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { NavLink } from "react-router";
import useAuth from "../hooks/useAuth";
import LoginDialog from "./auth/LoginDialog";
import RegisterDialog from "./auth/RegisterDialog";
import nrsLogo from "../assets/NRS-Logo.webp";

export default function Header() {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const { user, logout } = useAuth();
  const linkStyle = ({ isActive }) => ({
    textDecoration: "none",
    color: isActive ? theme.palette.primary.main : theme.palette.common.white,
    fontWeight: isActive ? 700 : 500,
  });
  const mainNRSlinkStyle = () => ({
    textDecoration: "none",
    color: theme.palette.common.white,
    fontWeight: 500,
    paddingLeft: 24,
    borderLeft:'1px solid white'
  });

  return (
    <>
      {/* Desktop: two-row layout */}
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        square
        sx={{ borderRadius: 0 }}
      >
        <Toolbar
          sx={{
            display: { xs: "none", md: "flex" },
            justifyContent: "flex-end",
            gap: 2,
            minHeight: 48,
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
          }}
        >
          {user ? (
            <>
              <Button
                size="small"
                component={NavLink}
                to="/account"
                color="primary"
                sx={{ fontWeight: 800 }}
              >
                Profile
              </Button>
              <Button size="small" variant="contained" color="primary" onClick={logout}>
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button size="small" color="secondary" onClick={() => setLoginOpen(true)}>
                Login
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => setRegisterOpen(true)}
              >
                Register
              </Button>
            </>
          )}
        </Toolbar>

        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          }}
        >
          <Box
            component={NavLink}
            to="/"
            style={linkStyle({ isActive: false })}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, textDecoration: "none" }}
          >
            <Box
              component="img"
              src={nrsLogo}
              alt="NRS logo"
              sx={{ width: 60, height:60, objectFit: "contain" }}
            />
            <Typography variant="h5" component="span" sx={{ fontWeight: 800 }}>
              ResourceNAV
            </Typography>
          </Box>

        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3, alignItems: "center" }}>
          <NavLink to="/" style={linkStyle}>Home</NavLink>
          <NavLink to="/companies" style={linkStyle}>Companies</NavLink>
          <NavLink to="/rankings" style={linkStyle}>Rankings</NavLink>
          <NavLink to="/screener" style={linkStyle}>Screener</NavLink>
          <NavLink to="/about" style={linkStyle}>About</NavLink>
          <NavLink to="/contact" style={linkStyle}>Contact</NavLink>
          <NavLink to="https://naturalresourcestocks.net" style={mainNRSlinkStyle} target="_blank" rel="noopener noreferrer">NaturalResourceStocks.net ↗</NavLink>
        </Box>

          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            sx={{ display: { xs: "inline-flex", md: "none" } }}
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile: single row + drawer with nav + account actions */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          BackdropProps: { sx: { backgroundColor: "rgba(0,0,0,0.35)" } },
        }}
        PaperProps={{
          sx: {
            width: 280,
            p: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
          <Typography variant="h6" fontWeight={800}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)} aria-label="close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {[
            { to: "/", label: "Home" },
            { to: "/companies", label: "Companies" },
            { to: "/rankings", label: "Rankings" },
            { to: "/screener", label: "Screener" },
            { to: "/about", label: "About" },
            { to: "/contact", label: "Contact" },
            { to: "https://naturalresourcestocks.net", label: "NaturalResourceStocks.net ↗" },
          ].map((item) => (
            <ListItem key={item.to} disablePadding>
              <ListItemButton
                component={NavLink}
                to={item.to}
                onClick={() => setMobileOpen(false)}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 1 }} />
        <List sx={{ mt: "auto" }}>
          {user ? (
            <>
              <ListItem disablePadding>
                <ListItemButton component={NavLink} to="/account" onClick={() => setMobileOpen(false)}>
                  <ListItemText primary="Profile" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { setMobileOpen(false); logout(); }}>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setMobileOpen(false);
                    setLoginOpen(true);
                  }}
                >
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setMobileOpen(false);
                    setRegisterOpen(true);
                  }}
                >
                  <ListItemText primary="Register" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      <RegisterDialog open={registerOpen} onClose={() => setRegisterOpen(false)} />
    </>
  );
}
